import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* Un mois en calendrier, comme Calendly (Jen, 30.10.2026) : les jours où il y a quelque chose
   sont en couleur et cliquables, les autres en gris. Sert à la réservation des cours privés
   (site) et au calendrier de tous les profs (console, app école).
   `jours` : { "2026-11-05": { points: ["#7C3AED", …], titre: "…" } } — un jour absent = gris.
   `mois` : "2026-11" ; `onMois(m)` change de mois (flèches désactivées hors de `min`…`max`).

   `agenda` (console, app école, app des profs — Jen, 02.11.2026 : « le vert partout n'apporte
   rien ») : UNE seule couleur forte, le vert plein des jours qui ont un cours réservé
   (`ton: "reserve"`, `nb` : leur nombre, en pastille dès 2) ; les autres jours restent neutres
   (`ton: "eteint"` : gris clair, aucun prof disponible). Le jour choisi est cerclé de noir,
   aujourd'hui souligné : trois repères qui se combinent sans se confondre. Le site (réservation
   par les familles) garde son sens : en vert, les jours où l'on PEUT réserver. */
export const VERT_RESERVE = "#15803D";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre",
  "octobre", "novembre", "décembre"];
const ENTETES = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const moisDe = (jour) => String(jour || "").slice(0, 7);
export const decalerMois = (m, n) => {
  const [a, b] = m.split("-").map(Number);
  const d = new Date(a, b - 1 + n, 1, 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/* Un jour de l'agenda : vert plein s'il a un cours réservé (plus pâle une fois passé), sinon
   neutre ; cerclé s'il est choisi ; un trait sous le numéro pour aujourd'hui. */
const JourAgenda = ({ j, x, on, auj, onChoisir, testid }) => {
  const reserve = x.ton === "reserve";
  const n = Number(j.slice(8));
  return (
    <button type="button" onClick={() => onChoisir(j)} title={x.titre || undefined} aria-pressed={on}
      aria-label={`${n}${x.titre ? ` · ${x.titre}` : ""}${auj ? " · aujourd'hui" : ""}`}
      className={`aspect-square rounded-full grid place-items-center text-sm relative transition-colors ${reserve ? "" : "hover:bg-[#F1F5F9]"}`}
      style={{
        ...(reserve ? { background: VERT_RESERVE, color: "#fff", fontWeight: 700, opacity: x.passe ? 0.5 : 1 }
          : { color: auj ? "#0E7490" : x.ton === "eteint" ? "#CBD5E1" : "#334155", fontWeight: auj ? 700 : 500 }),
        ...(on ? { boxShadow: "0 0 0 2px #fff, 0 0 0 4px #0F172A" } : null),
      }}
      data-testid={`${testid}-${j}`} data-ton={x.ton || ""}>
      <span>{n}</span>
      {auj && <span className="absolute bottom-[15%] left-1/2 -translate-x-1/2 w-3 h-[2px] rounded-full" style={{ background: reserve ? "#fff" : "var(--primary)" }} />}
      {reserve && x.nb > 1 && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full grid place-items-center text-[10px] font-bold leading-none bg-white tabular-nums"
          style={{ color: VERT_RESERVE, border: `1.5px solid ${VERT_RESERVE}` }} data-testid={`${testid}-${j}-nb`}>{x.nb}</span>
      )}
    </button>
  );
};

export default function MoisCalendrier({ mois, onMois, jours, choisi, onChoisir, min = "", max = "", couleur = "var(--primary)", aujourdhui = iso(new Date()), testid = "mois-calendrier", agenda = false }) {
  const [a, b] = mois.split("-").map(Number);
  const premier = new Date(a, b - 1, 1, 12);
  const decalage = (premier.getDay() + 6) % 7;          // lundi en premier
  const nb = new Date(a, b, 0).getDate();
  const cases = [...Array(decalage).fill(null), ...Array.from({ length: nb }, (_, k) => iso(new Date(a, b - 1, k + 1, 12)))];
  const avant = !min || decalerMois(mois, -1) >= moisDe(min);
  const apres = !max || decalerMois(mois, 1) <= moisDe(max);
  return (
    <div className="w-full max-w-[380px] mx-auto" data-testid={testid}>
      <div className="flex items-center justify-center gap-4 mb-3">
        <button type="button" disabled={!avant} onClick={() => onMois(decalerMois(mois, -1))} aria-label="Mois précédent"
          className="w-9 h-9 rounded-full grid place-items-center disabled:opacity-25 hover:bg-[var(--primary-tint)]" data-testid={`${testid}-avant`}><ChevronLeft size={18} /></button>
        <div className="font-semibold min-w-[140px] text-center">{MOIS[b - 1]} {a}</div>
        <button type="button" disabled={!apres} onClick={() => onMois(decalerMois(mois, 1))} aria-label="Mois suivant"
          className="w-9 h-9 rounded-full grid place-items-center disabled:opacity-25 hover:bg-[var(--primary-tint)]" data-testid={`${testid}-apres`}><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-[var(--muted)] mb-1">
        {ENTETES.map((e) => <div key={e}>{e}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cases.map((j, k) => {
          if (!j) return <div key={`v${k}`} />;
          const x = jours[j];
          const on = choisi === j;
          const n = Number(j.slice(8));
          if (agenda) return <JourAgenda key={j} j={j} x={x || { ton: "eteint" }} on={on} auj={j === aujourdhui} onChoisir={onChoisir} testid={testid} />;
          if (!x) {
            return (
              <div key={j} className="aspect-square grid place-items-center text-sm rounded-full" style={{ color: "#94A3B8" }}>
                <span style={j === aujourdhui ? { textDecoration: "underline" } : undefined}>{n}</span>
              </div>
            );
          }
          // `ton: "neutre"` : un jour touchable sans rien de libre (l'agenda d'un prof).
          const repos = x.ton === "neutre" ? { background: "#F1F5F9", color: "#64748B" } : { background: "#DCFCE7", color: "#166534" };
          return (
            <button key={j} type="button" onClick={() => onChoisir(j)} title={x.titre || undefined} aria-pressed={on}
              className="aspect-square rounded-full grid place-items-center text-sm font-semibold relative transition-colors"
              style={on ? { background: couleur, color: "#fff" } : repos}
              data-testid={`${testid}-${j}`}>
              <span>{n}</span>
              {x.points?.length > 0 && (
                <span className="absolute bottom-[14%] left-1/2 -translate-x-1/2 flex gap-0.5">
                  {x.points.slice(0, 4).map((c, i) => <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: on ? "#fff" : c }} />)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
